import React from 'react';
import { manualSessionActive } from '../hooks/useAuth';
import { User } from 'firebase/auth';
import { useToast } from './Toast';

interface AuthenticatedSelectProps {
  user: User | null;
  userProfile: any;
  onSessionStart: () => Promise<boolean>;
  onShowAuthModal: () => void;
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const AuthenticatedSelect: React.FC<AuthenticatedSelectProps> = ({
  user,
  userProfile,
  onSessionStart,
  onShowAuthModal,
  value,
  onChange,
  className = '',
  disabled = false,
  children
}) => {
  const { showToast } = useToast();

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!user) {
      onShowAuthModal();
      return;
    }

    if (!userProfile) return;

    // Read the value BEFORE any await: while the session write is in flight React
    // restores the controlled input, and reading it afterwards lost the first edit.
    const selectedValue = e.target.value;

    // Start a manual session unless one is open (expired ones count as closed)
    if (!manualSessionActive(userProfile)) {
      const canStartSession = await onSessionStart();
      if (!canStartSession) {
        showToast(`You've reached your monthly limit of 3 manual input sessions. ${userProfile.subscriptionTier === 'free' ? 'Upgrade to Premium for unlimited access.' : ''}`, { type: 'warning' });
        return;
      }
    }

    onChange(selectedValue === '' ? null : selectedValue);
  };

  const isDisabled = !user || disabled;

  return (
    <select
      value={value ?? ''}
      onChange={handleChange}
      className={`${className || 'input-field'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      disabled={isDisabled}
    >
      {!user ? (
        <option value="">Sign in required</option>
      ) : (
        children
      )}
    </select>
  );
};

export default AuthenticatedSelect;
